import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  ElementRef,
  ErrorHandler,
  inject,
  Inject,
  Injectable,
  Optional,
  PLATFORM_ID
} from '@angular/core';
import { createErrorHandler, SentryErrorHandler } from '@sentry/angular';

import { SENTRY_ENABLED } from '@usheru-hff/shared/tokens';

const TTL = 1000 * 60 * 60 * 24; // 24 hours
const CHUNK_RELOAD_KEY = '__app_reload_attempts__';
const CHUNK_RELOAD_PATTERN = /Failed to fetch dynamically imported module:/;
const MAX_RELOAD_ATTEMPS = 3;
interface ChunkErrorInfo {
  attempts: number;
  lastAttempt: number; // timestamp in ms
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly sentryErrorHandler: SentryErrorHandler | undefined;

  private readonly isPlatformBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));

  constructor(
    @Inject(SENTRY_ENABLED) private sentryEnabled: boolean,
    @Optional() private changeDetector: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document
  ) {
    if (sentryEnabled) {
      this.sentryErrorHandler = createErrorHandler();
    }
  }

  handleError(error: any): void {
    const name = error?.name ?? '';

    if (this.isChunkLoadError(error)) {
      const attempts = this.incrementChunkError();

      if (attempts < MAX_RELOAD_ATTEMPS) {
        this.propagateError({
          name,
          message: `Chunk load error: Attempt: ${attempts} | ${this.stringifyError(error)}`
        });
        // This will reload the application and in most of the cases will solve the problem.
        window.location.reload();
        return;
      }
      // else
      this.clearChunkError();
      this.propagateError({
        name,
        message: `Too many reload attempts. Not reloading again. | ${this.stringifyError(error)}`
      });
      this.createMaxAttempDialog();
      this.changeDetector?.detectChanges();
      this.document?.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Omit if empty array
    if (Array.isArray(error) && error.length === 0) {
      return;
    }
    // Forward to Sentry
    this.propagateError(error);
  }

  // We want to control an errors for missing JS files, which mean the application is an older state than the latest deployed.
  // Once it is controlled, the application should restart.
  // To not ending-up in a loop there is a break-trough where it will ask the user to clear their cache.
  // `Failed to fetch dynamically imported module: https://panoptico-ssr.develop.usheru.com/src-6ET6X3DT.js`
  private isChunkLoadError(error): boolean {
    const message = error?.message ?? '';
    return CHUNK_RELOAD_PATTERN.test(message) && this.isPlatformBrowser && message.includes(location.origin);
  }

  private propagateError(error?: any): void {
    if (this.sentryEnabled && this.sentryErrorHandler) {
      this.sentryErrorHandler.handleError(error);
    }
  }

  private stringifyError(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    try {
      return JSON.stringify(err);
    } catch {
      return 'Unknown error';
    }
  }

  /**
   * Dialog for extreme cases when the app fails to load after 2 reload attempts.
   * The user shouldn't reach this point, but if they do, we need to inform them that the app is broken.
   */
  private createMaxAttempDialog() {
    const dialogEl = document.createElement('dialog') as HTMLDialogElement;
    dialogEl.setAttribute('role', 'alertdialog');
    dialogEl.setAttribute('aria-live', 'assertive');
    dialogEl.setAttribute('aria-atomic', 'true');
    dialogEl.setAttribute('aria-modal', 'true');
    dialogEl.setAttribute('open', 'true');
    dialogEl.style.zIndex = '10000';
    dialogEl.style.top = '0';
    //TODO add transition. Change the message
    dialogEl.innerHTML = `
    <p style="font-size: 16px; line-height: 1.4;">
      It looks like something went wrong while loading the application.<br><br>
      Please try refreshing the page.<br><br>
      If the problem continues, clear your browser cache and reload.
    </p>
  `;
    document.body.appendChild(dialogEl);
  }

  private getChunkErrorInfo(): ChunkErrorInfo {
    const raw = localStorage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return { attempts: 0, lastAttempt: 0 };

    try {
      const data = JSON.parse(raw) as ChunkErrorInfo;
      const now = Date.now();

      // If the last error is too old, reset it
      if (now - data.lastAttempt > TTL) {
        localStorage.removeItem(CHUNK_RELOAD_KEY);
        return { attempts: 0, lastAttempt: 0 };
      }

      return data;
    } catch {
      localStorage.removeItem(CHUNK_RELOAD_KEY);
      return { attempts: 0, lastAttempt: 0 };
    }
  }

  private setChunkErrorInfo(attempts: number): void {
    const data: ChunkErrorInfo = {
      attempts,
      lastAttempt: Date.now()
    };
    localStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify(data));
  }

  private incrementChunkError(): number {
    const current = this.getChunkErrorInfo();
    const newAttempts = current.attempts + 1;
    this.setChunkErrorInfo(newAttempts);
    return newAttempts;
  }

  private clearChunkError(): void {
    localStorage.removeItem(CHUNK_RELOAD_KEY);
  }
}
